import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Calendar,
  ExternalLink,
  Plus,
  Pill,
  Edit2,
  Eye,
  Info,
  MapPin,
  Phone,
  Search,
} from "lucide-react";
import { PrescriptionModal } from "../PrescriptionApproval/components/PrescriptionModal";
import {
  EmergencySupplyMedicationModal,
  EmergencySupplyMedicationItem,
} from "@/components/ui/EmergencySupplyMedicationModal";
import { PharmacySearchModal } from "../ui/PharmacySearchModal";
import type { PharmacySearchResult } from "./pharmacyService";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { generatePrescriptionHTML } from "@/lib/prescriptionPdf";
import {
  getNhs111EmergencySupplyFormData,
  formatFormDataAsTemplate,
  useNhs111EmergencySupplyForm,
} from "./UseNhs111EmergencySupplyFormData";

interface NHS111EmergencySupplyFormData {
  patientConsent: "yes" | "no" | "";
  consultationDate: string;
  consultationTime: string;
  consultationMethod: "face-to-face" | "telephone" | "video" | "";
  patientAccompanied: "yes" | "no" | "";
  accompaniedRelationship: string;
  accompaniedRelationshipOther: string;
  reasonForRequest: string;
  reasonForRequestOther: string;
  ncrsRecordAccessed: "yes" | "no" | "";
  electronicPrescriptionAvailable: "yes" | "no" | "";
  outcomes: string;
  outcomesOther: string;
  referralDestination: string;
  referralDestinationOther: string;
  escalationDestination: string;
  escalationDestinationOther: string;
  onwardReferralReason: string;
  onwardReferralDate: string;
  onwardReferralTime: string;
  destinationOdsCode: string;
  adviceGiven: boolean;
  adviceProvidedTo: string;
  planAndRequestedActions: string;
  internalNotes: string;
  forwardOutOfStock: "yes" | "no" | "";
  forwardedPharmacy?: PharmacySearchResult;
  referralOrigin: string;
  referralOriginOther: string;
  referralContactDetails: string;
  referralDate: string;
  referralTime: string;
}

interface Prescription {
  id: string;
  prescriptionItems: any[];
  prescriptionCategory?: string;
  status?: string;
  issuedAt?: string;
  printedAt?: string;
  pharmacy?: string;
}

interface PatientData {
  postcode?: string;
  [key: string]: any;
}

interface NHS111EmergencySupplyFormProps {
  isDarkMode?: boolean;
  onSave?: (data: NHS111EmergencySupplyFormData) => void;
  onComplete?: (
    data: NHS111EmergencySupplyFormData,
    prescriptions: Prescription[],
    emergencySupplyMedications?: EmergencySupplyMedicationItem[],
  ) => void;
  initialData?: Partial<NHS111EmergencySupplyFormData>;
  patientData?: PatientData;
  fromKanbanOrList?: boolean;
  consultationStatus?: number;
}

export function NHS111EmergencySupplyForm({
  isDarkMode = false,
  onSave,
  onComplete,
  initialData = {},
  patientData,
  fromKanbanOrList = false,
  consultationStatus,
}: NHS111EmergencySupplyFormProps) {
  const { addConsultationSummaryLoader } = useNhs111EmergencySupplyForm();

  const [formData, setFormData] = useState<NHS111EmergencySupplyFormData>({
    patientConsent: initialData.patientConsent || "",
    consultationDate:
      initialData.consultationDate || new Date().toISOString().split("T")[0],
    consultationTime:
      initialData.consultationTime || new Date().toTimeString().slice(0, 5),
    consultationMethod: initialData.consultationMethod || "",
    patientAccompanied: initialData.patientAccompanied || "",
    accompaniedRelationship: initialData.accompaniedRelationship || "",
    accompaniedRelationshipOther:
      initialData.accompaniedRelationshipOther || "",
    reasonForRequest: initialData.reasonForRequest || "",
    reasonForRequestOther: initialData.reasonForRequestOther || "",
    ncrsRecordAccessed: initialData.ncrsRecordAccessed || "",
    electronicPrescriptionAvailable:
      initialData.electronicPrescriptionAvailable || "",
    outcomes: initialData.outcomes || "",
    outcomesOther: initialData.outcomesOther || "",
    referralDestination: initialData.referralDestination || "",
    referralDestinationOther: initialData.referralDestinationOther || "",
    escalationDestination: initialData.escalationDestination || "",
    escalationDestinationOther: initialData.escalationDestinationOther || "",
    onwardReferralReason: initialData.onwardReferralReason || "",
    onwardReferralDate: initialData.onwardReferralDate || "",
    onwardReferralTime: initialData.onwardReferralTime || "",
    destinationOdsCode: initialData.destinationOdsCode || "",
    adviceGiven: initialData.adviceGiven || false,
    adviceProvidedTo: initialData.adviceProvidedTo || "",
    planAndRequestedActions: initialData.planAndRequestedActions || "",
    internalNotes: initialData.internalNotes || "",
    forwardOutOfStock: initialData.forwardOutOfStock || "",
    referralOrigin: initialData.referralOrigin || "",
    referralOriginOther: initialData.referralOriginOther || "",
    referralContactDetails: initialData.referralContactDetails || "",
    referralDate: initialData.referralDate || "",
    referralTime: initialData.referralTime || "",
  });

  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [editingPrescriptionIndex, setEditingPrescriptionIndex] = useState<
    number | null
  >(null);
  const [viewingPrescriptionIndex, setViewingPrescriptionIndex] = useState<
    number | null
  >(null);
  const [emergencySupplyMedications, setEmergencySupplyMedications] = useState<
    EmergencySupplyMedicationItem[]
  >([]);

  // Check if any medications are marked as "not-in-stock"
  const hasOutOfStockMedications = emergencySupplyMedications.some(
    (med) => med.notSuppliedReason === "not-in-stock",
  );

  const [selectedPharmacy, setSelectedPharmacy] =
    useState<PharmacySearchResult | null>(
      initialData.forwardedPharmacy || null,
    );

  const [isDataSharingPopupOpen, setIsDataSharingPopupOpen] = useState(false);

  // Get patient postcode for pharmacy search
  const patientPostcode = patientData?.postcode || "";

  // Get form data
  const formData_source = getNhs111EmergencySupplyFormData();

  const handleChange = (
    field: keyof NHS111EmergencySupplyFormData,
    value: any,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePrescriptionSave = (prescription: any) => {
    if (editingPrescriptionIndex !== null) {
      // Update existing prescription
      setPrescriptions((prev) =>
        prev.map((p, index) =>
          index === editingPrescriptionIndex ? prescription : p,
        ),
      );
      setEditingPrescriptionIndex(null);
    } else {
      // Add new prescription
      setPrescriptions((prev) => [...prev, prescription]);
    }
  };

  const handleMedicationSave = (medication: EmergencySupplyMedicationItem) => {
    // Add new medication
    setEmergencySupplyMedications((prev) => [...prev, medication]);
  };

  const handleMedicationUpdate = (
    medication: EmergencySupplyMedicationItem,
    index: number,
  ) => {
    // Update existing medication
    setEmergencySupplyMedications((prev) =>
      prev.map((m, i) => (i === index ? medication : m)),
    );
  };

  const handleRemoveMedication = (index: number) => {
    setEmergencySupplyMedications((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEditPrescription = (index: number) => {
    setEditingPrescriptionIndex(index);
  };

  const handleViewPrescription = (index: number) => {
    setViewingPrescriptionIndex(index);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Include selected pharmacy and emergency supply medications in form data
    const finalFormData = {
      ...formData,
      forwardedPharmacy: selectedPharmacy || undefined,
      emergencySupplyMedications:
        emergencySupplyMedications.length > 0
          ? emergencySupplyMedications
          : undefined,
      hasOutOfStockMedications,
    };

    // Format the data as template with label-value pairs
    const formattedOutput = formatFormDataAsTemplate(
      finalFormData,
      formData_source,
    );
    if (onSave) {
      onSave(finalFormData);
    }
    if (onComplete) {
      onComplete(finalFormData, prescriptions, emergencySupplyMedications);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Section 1: Referral Details (only shown when NOT from Kanban/Listview) */}
      {!fromKanbanOrList && (
        <div
          className={`border rounded-2xl p-4 transition-colors duration-300 ${
            isDarkMode
              ? "border-gray-700 bg-gray-800"
              : "border-gray-200 bg-white"
          }`}
        >
          <div className="flex items-center gap-3 mb-4">
            <div
              className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold transition-colors duration-300 ${
                isDarkMode ? "bg-white text-black" : "bg-black text-white"
              }`}
            >
              1
            </div>
            <h3
              className={`text-sm font-semibold transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Referral Details
            </h3>
          </div>

          <div className="space-y-4">
            {/* Referral Origin */}
            <div className="space-y-2">
              <Label
                htmlFor="referral-origin"
                className={`text-sm ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Referral Origin
              </Label>
              <Select
                value={formData.referralOrigin}
                onValueChange={(value) => handleChange("referralOrigin", value)}
              >
                <SelectTrigger
                  id="referral-origin"
                  className={`${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <SelectValue placeholder="Select referral origin" />
                </SelectTrigger>
                <SelectContent
                  className={`${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600"
                      : "bg-white border-gray-300"
                  }`}
                >
                  {formData_source.selectOptions.referralOrigins.map(
                    (origin) => (
                      <SelectItem key={origin.value} value={origin.value}>
                        {origin.label}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Other Referral Origin - shown when Other is selected */}
            {formData.referralOrigin === "other" && (
              <div className="space-y-2">
                <Label
                  htmlFor="referral-origin-other"
                  className={`text-sm ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Please specify referral origin
                </Label>
                <Input
                  id="referral-origin-other"
                  type="text"
                  placeholder="Specify referral origin"
                  value={formData.referralOriginOther}
                  onChange={(e) =>
                    handleChange("referralOriginOther", e.target.value)
                  }
                  className={`${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                  }`}
                />
              </div>
            )}

            {/* Referral Contact Details */}
            <div className="space-y-2">
              <Label
                htmlFor="referral-contact"
                className={`text-sm ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Referral Contact Details
              </Label>
              <Input
                id="referral-contact"
                type="text"
                placeholder="Enter contact details"
                value={formData.referralContactDetails}
                onChange={(e) =>
                  handleChange("referralContactDetails", e.target.value)
                }
                className={`${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                }`}
              />
            </div>

            {/* Date and Time of Referral */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label
                  htmlFor="referral-date"
                  className={`text-sm ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Date of Referral
                </Label>
                <div className="relative">
                  <Input
                    id="referral-date"
                    type="date"
                    value={formData.referralDate}
                    onChange={(e) =>
                      handleChange("referralDate", e.target.value)
                    }
                    className={`${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                  <Calendar className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 pointer-events-none right-3 top-1/2" />
                </div>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="referral-time"
                  className={`text-sm ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Time of Referral
                </Label>
                <Input
                  id="referral-time"
                  type="time"
                  value={formData.referralTime}
                  onChange={(e) => handleChange("referralTime", e.target.value)}
                  className={`${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Section 1 or 2: Patient Consent (number depends on whether Referral Details is shown) */}
      <div
        className={`border rounded-2xl p-4 transition-colors duration-300 ${
          isDarkMode
            ? "border-gray-700 bg-gray-800"
            : "border-gray-200 bg-white"
        }`}
      >
        <div className="flex items-center gap-3 mb-4">
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold transition-colors duration-300 ${
              isDarkMode ? "bg-white text-black" : "bg-black text-white"
            }`}
          >
            {!fromKanbanOrList ? "2" : "1"}
          </div>
          <h3
            className={`text-sm font-semibold transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Patient Consent
          </h3>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label
              className={`text-sm ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Does the patient consent to this emergency supply?
            </Label>
            <button
              type="button"
              onClick={() => setIsDataSharingPopupOpen(true)}
              className={`flex items-center gap-1 text-xs font-medium hover:underline ${
                isDarkMode ? "text-blue-400" : "text-blue-600"
              }`}
            >
              <Info className="w-3 h-3" />
              View Data Sharing Details
            </button>
          </div>
          <RadioGroup
            value={formData.patientConsent}
            onValueChange={(value) => handleChange("patientConsent", value)}
            className="flex gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value="yes"
                id="consent-yes"
                className={`${
                  isDarkMode
                    ? "border-gray-600 text-white"
                    : "border-gray-300 text-black"
                }`}
              />
              <label
                htmlFor="consent-yes"
                className={`text-sm cursor-pointer ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Yes
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value="no"
                id="consent-no"
                className={`${
                  isDarkMode
                    ? "border-gray-600 text-white"
                    : "border-gray-300 text-black"
                }`}
              />
              <label
                htmlFor="consent-no"
                className={`text-sm cursor-pointer ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                No
              </label>
            </div>
          </RadioGroup>
        </div>
      </div>

      {/* Show remaining sections only if consent is "yes" */}
      {formData.patientConsent === "yes" && (
        <>
          {/* Section 2 or 3: Consultation Details (number depends on whether Referral Details is shown) */}
          <div
            className={`border rounded-2xl p-4 transition-colors duration-300 ${
              isDarkMode
                ? "border-gray-700 bg-gray-800"
                : "border-gray-200 bg-white"
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold transition-colors duration-300 ${
                  isDarkMode ? "bg-white text-black" : "bg-black text-white"
                }`}
              >
                {!fromKanbanOrList ? "3" : "2"}
              </div>
              <h3
                className={`text-sm font-semibold transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Consultation Details
              </h3>
            </div>

            <div className="space-y-4">
              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                {/* Date */}
                <div className="space-y-2">
                  <Label
                    htmlFor="date"
                    className={`text-sm ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Date
                  </Label>
                  <div className="relative">
                    <Input
                      id="date"
                      type="date"
                      value={formData.consultationDate}
                      onChange={(e) =>
                        handleChange("consultationDate", e.target.value)
                      }
                      className={`${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    />
                    <Calendar className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 pointer-events-none right-3 top-1/2" />
                  </div>
                </div>

                {/* Time */}
                <div className="space-y-2">
                  <Label
                    htmlFor="time"
                    className={`text-sm ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Time
                  </Label>
                  <Input
                    id="time"
                    type="time"
                    value={formData.consultationTime}
                    onChange={(e) =>
                      handleChange("consultationTime", e.target.value)
                    }
                    className={`${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>

              {/* Method of Consultation */}
              <div className="space-y-2">
                <Label
                  htmlFor="method"
                  className={`text-sm ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Method of Consultation
                </Label>
                <Select
                  value={formData.consultationMethod}
                  onValueChange={(value) =>
                    handleChange("consultationMethod", value)
                  }
                >
                  <SelectTrigger
                    id="method"
                    className={`${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <SelectValue placeholder="Select consultation method" />
                  </SelectTrigger>
                  <SelectContent
                    className={`${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600"
                        : "bg-white border-gray-300"
                    }`}
                  >
                    {formData_source.selectOptions.consultationMethods.map(
                      (method) => (
                        <SelectItem key={method.value} value={method.value}>
                          {method.label}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Patient Accompanied */}
              <div className="pt-4 space-y-3 border-t border-gray-200 dark:border-gray-700">
                <Label
                  className={`text-sm font-medium ${
                    isDarkMode ? "text-gray-200" : "text-gray-900"
                  }`}
                >
                  Patient Accompanied
                </Label>
                <RadioGroup
                  value={formData.patientAccompanied}
                  onValueChange={(value) => {
                    handleChange("patientAccompanied", value);
                    if (value === "no") {
                      handleChange("accompaniedRelationship", "");
                      handleChange("accompaniedRelationshipOther", "");
                    }
                  }}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="yes"
                      id="accompanied-yes"
                      className={`${
                        isDarkMode
                          ? "border-gray-600 text-white"
                          : "border-gray-300 text-black"
                      }`}
                    />
                    <label
                      htmlFor="accompanied-yes"
                      className={`text-sm cursor-pointer ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Yes
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="no"
                      id="accompanied-no"
                      className={`${
                        isDarkMode
                          ? "border-gray-600 text-white"
                          : "border-gray-300 text-black"
                      }`}
                    />
                    <label
                      htmlFor="accompanied-no"
                      className={`text-sm cursor-pointer ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      No
                    </label>
                  </div>
                </RadioGroup>

                {/* Relationship Dropdown - shown when Yes is selected */}
                {formData.patientAccompanied === "yes" && (
                  <div className="mt-3 space-y-2">
                    <Label
                      htmlFor="relationship"
                      className={`text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Relationship
                    </Label>
                    <Select
                      value={formData.accompaniedRelationship}
                      onValueChange={(value) => {
                        handleChange("accompaniedRelationship", value);
                        if (value !== "other") {
                          handleChange("accompaniedRelationshipOther", "");
                        }
                      }}
                    >
                      <SelectTrigger
                        id="relationship"
                        className={`${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      >
                        <SelectValue placeholder="Select relationship" />
                      </SelectTrigger>
                      <SelectContent
                        className={`${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600"
                            : "bg-white border-gray-300"
                        }`}
                      >
                        {formData_source.selectOptions.accompaniedRelationships.map(
                          (relationship) => (
                            <SelectItem
                              key={relationship.value}
                              value={relationship.value}
                            >
                              {relationship.label}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Other Relationship Text Input - shown when Other is selected */}
                {formData.patientAccompanied === "yes" &&
                  formData.accompaniedRelationship === "other" && (
                    <div className="mt-3 space-y-2">
                      <Label
                        htmlFor="relationship-other"
                        className={`text-sm ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Please specify
                      </Label>
                      <Input
                        id="relationship-other"
                        type="text"
                        placeholder="Specify relationship"
                        value={formData.accompaniedRelationshipOther}
                        onChange={(e) =>
                          handleChange(
                            "accompaniedRelationshipOther",
                            e.target.value,
                          )
                        }
                        className={`${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                            : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                        }`}
                      />
                    </div>
                  )}
              </div>
            </div>
          </div>

          {/* Section 3 or 4: Clinical Checks (number depends on whether Referral Details is shown) */}
          <div
            className={`border rounded-2xl p-4 transition-colors duration-300 ${
              isDarkMode
                ? "border-gray-700 bg-gray-800"
                : "border-gray-200 bg-white"
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold transition-colors duration-300 ${
                  isDarkMode ? "bg-white text-black" : "bg-black text-white"
                }`}
              >
                {!fromKanbanOrList ? "4" : "3"}
              </div>
              <h3
                className={`text-sm font-semibold transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Clinical Checks
              </h3>
            </div>

            <div className="space-y-4">
              {/* Reason for Request */}
              <div className="space-y-2">
                <Label
                  htmlFor="reason"
                  className={`text-sm ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Reason for request
                </Label>
                <Select
                  value={formData.reasonForRequest}
                  onValueChange={(value) => {
                    handleChange("reasonForRequest", value);
                    if (value !== "other") {
                      handleChange("reasonForRequestOther", "");
                    }
                  }}
                >
                  <SelectTrigger
                    id="reason"
                    className={`${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <SelectValue placeholder="Select reason for request" />
                  </SelectTrigger>
                  <SelectContent
                    className={`${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600"
                        : "bg-white border-gray-300"
                    }`}
                  >
                    {formData_source.selectOptions.reasonsForRequest.map(
                      (reason) => (
                        <SelectItem key={reason.value} value={reason.value}>
                          {reason.label}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Other Reason Text Input - shown when Other is selected */}
              {formData.reasonForRequest === "other" && (
                <div className="space-y-2">
                  <Label
                    htmlFor="reason-other"
                    className={`text-sm ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Please specify reason
                  </Label>
                  <Input
                    id="reason-other"
                    type="text"
                    placeholder="Specify reason for request"
                    value={formData.reasonForRequestOther}
                    onChange={(e) =>
                      handleChange("reasonForRequestOther", e.target.value)
                    }
                    className={`${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                    }`}
                  />
                </div>
              )}

              {/* NCRS Record Access */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label
                    className={`text-sm ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Has the patient's NCRS record been accessed?
                  </Label>
                  <a
                    href={formData_source.externalLinks.ncrs}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-1 text-xs font-medium hover:underline ${
                      isDarkMode ? "text-blue-400" : "text-blue-600"
                    }`}
                  >
                    {formData_source.labels.openNCRS}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <RadioGroup
                  value={formData.ncrsRecordAccessed}
                  onValueChange={(value) =>
                    handleChange("ncrsRecordAccessed", value)
                  }
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="yes"
                      id="ncrs-yes"
                      className={`${
                        isDarkMode
                          ? "border-gray-600 text-white"
                          : "border-gray-300 text-black"
                      }`}
                    />
                    <label
                      htmlFor="ncrs-yes"
                      className={`text-sm cursor-pointer ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Yes
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="no"
                      id="ncrs-no"
                      className={`${
                        isDarkMode
                          ? "border-gray-600 text-white"
                          : "border-gray-300 text-black"
                      }`}
                    />
                    <label
                      htmlFor="ncrs-no"
                      className={`text-sm cursor-pointer ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      No
                    </label>
                  </div>
                </RadioGroup>
              </div>

              {/* Electronic Prescription Available */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label
                    className={`text-sm ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Does the patient have an outstanding electronic prescription
                    available?
                  </Label>
                  <a
                    href={formData_source.externalLinks.epsTracker}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-1 text-xs font-medium hover:underline ${
                      isDarkMode ? "text-blue-400" : "text-blue-600"
                    }`}
                  >
                    {formData_source.labels.epsTracker}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <RadioGroup
                  value={formData.electronicPrescriptionAvailable}
                  onValueChange={(value) =>
                    handleChange("electronicPrescriptionAvailable", value)
                  }
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="yes"
                      id="eps-yes"
                      className={`${
                        isDarkMode
                          ? "border-gray-600 text-white"
                          : "border-gray-300 text-black"
                      }`}
                    />
                    <label
                      htmlFor="eps-yes"
                      className={`text-sm cursor-pointer ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Yes
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="no"
                      id="eps-no"
                      className={`${
                        isDarkMode
                          ? "border-gray-600 text-white"
                          : "border-gray-300 text-black"
                      }`}
                    />
                    <label
                      htmlFor="eps-no"
                      className={`text-sm cursor-pointer ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      No
                    </label>
                  </div>
                </RadioGroup>
              </div>
            </div>
          </div>

          {/* Section 4 or 5: Medicine Supply (number depends on whether Referral Details is shown) */}
          <div
            className={`border rounded-2xl p-4 transition-colors duration-300 ${
              isDarkMode
                ? "border-gray-700 bg-gray-800"
                : "border-gray-200 bg-white"
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold transition-colors duration-300 ${
                  isDarkMode ? "bg-white text-black" : "bg-black text-white"
                }`}
              >
                {!fromKanbanOrList ? "5" : "4"}
              </div>
              <h3
                className={`text-sm font-semibold transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Medicine Supply
              </h3>
            </div>

            <div className="space-y-4">
              {/* Add Medication Button */}
              <EmergencySupplyMedicationModal
                trigger={
                  <Button
                    type="button"
                    variant="outline"
                    className={`w-full ${
                      isDarkMode
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Medication Item
                  </Button>
                }
                onSave={handleMedicationSave}
                isDarkMode={isDarkMode}
              />

              {/* Medications List */}
              {emergencySupplyMedications.length > 0 && (
                <div className="mt-6 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4
                      className={`text-sm font-semibold ${
                        isDarkMode ? "text-gray-200" : "text-gray-900"
                      }`}
                    >
                      Emergency Supply Medications (
                      {emergencySupplyMedications.length})
                    </h4>
                  </div>
                  <div className="space-y-3">
                    {emergencySupplyMedications.map((medication, index) => (
                      <div
                        key={medication.id}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          medication.supplied === "yes"
                            ? isDarkMode
                              ? "border-green-800 bg-green-950/20"
                              : "border-green-200 bg-green-50/50"
                            : isDarkMode
                              ? "border-red-800 bg-red-950/20"
                              : "border-red-200 bg-red-50/50"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                                medication.supplied === "yes"
                                  ? isDarkMode
                                    ? "bg-green-900/50 text-green-300"
                                    : "bg-green-100 text-green-700"
                                  : isDarkMode
                                    ? "bg-red-900/50 text-red-300"
                                    : "bg-red-100 text-red-700"
                              }`}
                            >
                              {index + 1}
                            </div>
                          </div>
                          <div className="flex-1 space-y-3">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Pill
                                    className={`w-4 h-4 ${
                                      medication.supplied === "yes"
                                        ? isDarkMode
                                          ? "text-green-400"
                                          : "text-green-600"
                                        : isDarkMode
                                          ? "text-red-400"
                                          : "text-red-600"
                                    }`}
                                  />
                                  <h5
                                    className={`text-sm font-semibold ${
                                      isDarkMode
                                        ? "text-gray-100"
                                        : "text-gray-900"
                                    }`}
                                  >
                                    {medication.medication}
                                  </h5>
                                </div>
                                <span
                                  className={`inline-block px-2.5 py-1 text-xs font-medium rounded-full ${
                                    medication.supplied === "yes"
                                      ? isDarkMode
                                        ? "bg-green-900/40 text-green-300 border border-green-700"
                                        : "bg-green-100 text-green-700 border border-green-300"
                                      : isDarkMode
                                        ? "bg-red-900/40 text-red-300 border border-red-700"
                                        : "bg-red-100 text-red-700 border border-red-300"
                                  }`}
                                >
                                  {medication.supplied === "yes"
                                    ? "✓ Supplied"
                                    : "✗ Not Supplied"}
                                </span>
                              </div>
                              <div className="flex gap-1.5">
                                <EmergencySupplyMedicationModal
                                  trigger={
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className={`h-8 px-3 text-xs ${
                                        isDarkMode
                                          ? "border-gray-600 hover:bg-gray-700 text-gray-300"
                                          : "border-gray-300 hover:bg-gray-100 text-gray-700"
                                      }`}
                                      title="Edit medication"
                                    >
                                      <Edit2 className="w-3.5 h-3.5" />
                                    </Button>
                                  }
                                  onSave={(med) =>
                                    handleMedicationUpdate(med, index)
                                  }
                                  editMode={medication}
                                  isDarkMode={isDarkMode}
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleRemoveMedication(index)}
                                  className={`h-8 px-3 text-xs border-red-300 hover:bg-red-50 text-red-600 dark:border-red-700 dark:hover:bg-red-950/30 dark:text-red-400`}
                                  title="Remove medication"
                                >
                                  <Plus className="w-3.5 h-3.5 rotate-45" />
                                </Button>
                              </div>
                            </div>

                            {medication.supplied === "yes" ? (
                              <div className="space-y-2">
                                <div className="grid grid-cols-2 gap-3">
                                  <div
                                    className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                  >
                                    <span
                                      className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                                    >
                                      Pack/Brand:
                                    </span>
                                    <div className="mt-0.5">
                                      {medication.packBrand}
                                    </div>
                                  </div>
                                  <div
                                    className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                  >
                                    <span
                                      className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                                    >
                                      Quantity:
                                    </span>
                                    <div className="mt-0.5">
                                      {medication.quantity}
                                    </div>
                                  </div>
                                  <div
                                    className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                  >
                                    <span
                                      className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                                    >
                                      Days Supply:
                                    </span>
                                    <div className="mt-0.5">
                                      {medication.daysSupply} days
                                    </div>
                                  </div>
                                </div>
                                <div
                                  className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                >
                                  <span
                                    className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                                  >
                                    Dose Instructions:
                                  </span>
                                  <div
                                    className={`mt-1 p-2 rounded ${isDarkMode ? "bg-gray-800/50" : "bg-white/80"}`}
                                  >
                                    {medication.doseInstructions}
                                  </div>
                                </div>
                                {medication.notes && (
                                  <div
                                    className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                  >
                                    <span
                                      className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                                    >
                                      Additional Notes:
                                    </span>
                                    <div
                                      className={`mt-1 p-2 rounded italic ${isDarkMode ? "bg-gray-800/50" : "bg-white/80"}`}
                                    >
                                      {medication.notes}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div
                                className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                              >
                                <span
                                  className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                                >
                                  Reason:
                                </span>
                                <div
                                  className={`mt-1 p-2 rounded ${isDarkMode ? "bg-gray-800/50" : "bg-white/80"}`}
                                >
                                  {medication.notSuppliedReason ===
                                  "schedule-controlled"
                                    ? "A: Item not able to be supplied under emergency supply regulations (e.g. Schedule 1, 2 or 3 controlled drug)"
                                    : medication.notSuppliedReason ===
                                        "eps-prescription"
                                      ? "B: EPS Prescription dispensed for patient"
                                      : medication.notSuppliedReason ===
                                          "not-necessary"
                                        ? "C: Pharmacist determined that supply not necessary (e.g. not clinically appropriate or concern about abuse of service)"
                                        : medication.notSuppliedReason ===
                                            "not-in-stock"
                                          ? "D: Item not in stock"
                                          : medication.notSuppliedReason ===
                                              "patient-bought"
                                            ? "F: Patient bought item"
                                            : medication.notSuppliedReason ===
                                                "other"
                                              ? `Other: ${medication.notSuppliedReasonOther}`
                                              : medication.notSuppliedReason}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Forward Out of Stock Medications Section */}
          {hasOutOfStockMedications && (
            <div
              className={`border rounded-2xl p-4 transition-colors duration-300 ${
                isDarkMode
                  ? "border-gray-700 bg-gray-800"
                  : "border-gray-200 bg-white"
              }`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold transition-colors duration-300 ${
                    isDarkMode ? "bg-white text-black" : "bg-black text-white"
                  }`}
                >
                  <Pill className="w-4 h-4" />
                </div>
                <h3
                  className={`text-sm font-semibold transition-colors duration-300 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Forward out of stock medication(s)
                </h3>
              </div>

              <div className="space-y-4">
                {/* Info Panel */}
                <Alert
                  className={
                    isDarkMode
                      ? "bg-blue-950/20 border-blue-800"
                      : "bg-blue-50 border-blue-200"
                  }
                >
                  <Info
                    className={`h-4 w-4 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}
                  />
                  <AlertDescription
                    className={`text-xs ${isDarkMode ? "text-blue-200" : "text-blue-900"}`}
                  >
                    {formData_source.messages.forwardOutOfStockInfo}
                  </AlertDescription>
                </Alert>

                {/* Question */}
                <div className="space-y-3">
                  <Label
                    className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Do you want to forward out of stock medication to another
                    pharmacy?
                  </Label>
                  <RadioGroup
                    value={formData.forwardOutOfStock}
                    onValueChange={(value: "yes" | "no") =>
                      handleChange("forwardOutOfStock", value)
                    }
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="forward-yes" />
                      <Label
                        htmlFor="forward-yes"
                        className={`text-sm cursor-pointer transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Yes
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="forward-no" />
                      <Label
                        htmlFor="forward-no"
                        className={`text-sm cursor-pointer transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        No
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Pharmacy Search - only show when Yes is selected */}
                {formData.forwardOutOfStock === "yes" && (
                  <div className="pt-3 space-y-3 border-t">
                    <Label
                      className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Select Pharmacy for Referral
                    </Label>

                    {selectedPharmacy ? (
                      <div
                        className={`p-4 rounded-lg border-2 ${
                          isDarkMode
                            ? "border-blue-700 bg-blue-950/30"
                            : "border-blue-300 bg-blue-50"
                        }`}
                      >
                        <div className="space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h5
                                className={`font-semibold text-sm ${
                                  isDarkMode ? "text-gray-100" : "text-gray-900"
                                }`}
                              >
                                {selectedPharmacy.name}
                              </h5>
                              <p
                                className={`text-xs mt-1 ${
                                  isDarkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                <MapPin className="inline w-3 h-3 mr-1" />
                                {selectedPharmacy.address},{" "}
                                {selectedPharmacy.postcode}
                              </p>
                              <p
                                className={`text-xs mt-1 ${
                                  isDarkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                <Phone className="inline w-3 h-3 mr-1" />
                                {selectedPharmacy.telephone}
                              </p>
                              <p
                                className={`text-xs mt-1 ${
                                  isDarkMode ? "text-gray-400" : "text-gray-600"
                                }`}
                              >
                                Distance: {selectedPharmacy.distance} miles
                              </p>
                            </div>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                isDarkMode
                                  ? "bg-green-900/40 text-green-300"
                                  : "bg-green-100 text-green-700"
                              }`}
                            >
                              Selected
                            </span>
                          </div>
                          <div className="flex gap-2 pt-2">
                            <PharmacySearchModal
                              trigger={
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className={`text-xs ${
                                    isDarkMode
                                      ? "border-gray-600 hover:bg-gray-700 text-gray-300"
                                      : "border-gray-300 hover:bg-gray-100 text-gray-700"
                                  }`}
                                >
                                  Change Pharmacy
                                </Button>
                              }
                              onSelect={setSelectedPharmacy}
                              isDarkMode={isDarkMode}
                              defaultPostcode={patientPostcode}
                              showServiceTypeFilter={false}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedPharmacy(null)}
                              className={`text-xs border-red-300 hover:bg-red-50 text-red-600 dark:border-red-700 dark:hover:bg-red-950/30 dark:text-red-400`}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <PharmacySearchModal
                        trigger={
                          <Button
                            type="button"
                            variant="outline"
                            className={`w-full ${
                              isDarkMode
                                ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                                : "border-gray-300 text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            <Search className="w-4 h-4 mr-2" />
                            Search for Pharmacy
                          </Button>
                        }
                        onSelect={setSelectedPharmacy}
                        isDarkMode={isDarkMode}
                        defaultPostcode={patientPostcode}
                        showServiceTypeFilter={false}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Section 5 or 6: Outcomes (number depends on whether Referral Details is shown) */}
          <div
            className={`border rounded-2xl p-4 transition-colors duration-300 ${
              isDarkMode
                ? "border-gray-700 bg-gray-800"
                : "border-gray-200 bg-white"
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold transition-colors duration-300 ${
                  isDarkMode ? "bg-white text-black" : "bg-black text-white"
                }`}
              >
                {!fromKanbanOrList ? "6" : "5"}
              </div>
              <h3
                className={`text-sm font-semibold transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Outcomes
              </h3>
            </div>

            <div className="space-y-3">
              <RadioGroup
                value={formData.outcomes}
                onValueChange={(value) => {
                  handleChange("outcomes", value);
                  if (value !== "other") {
                    handleChange("outcomesOther", "");
                  }
                }}
                className="flex flex-col gap-3"
              >
                {formData_source.selectOptions.outcomes.map((outcome) => (
                  <div
                    key={outcome.value}
                    className="flex items-center space-x-2"
                  >
                    <RadioGroupItem
                      value={outcome.value}
                      id={`outcome-${outcome.value}`}
                      className={`${
                        isDarkMode
                          ? "border-gray-600 text-white"
                          : "border-gray-300 text-black"
                      }`}
                    />
                    <label
                      htmlFor={`outcome-${outcome.value}`}
                      className={`text-sm cursor-pointer ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      {outcome.label}
                    </label>
                  </div>
                ))}
              </RadioGroup>

              {/* Other Outcome Text Input - shown when Other is selected */}
              {formData.outcomes === "other" && (
                <div className="mt-3 space-y-2">
                  <Label
                    htmlFor="outcome-other-specify"
                    className={`text-sm ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Please specify outcome
                  </Label>
                  <Input
                    id="outcome-other-specify"
                    type="text"
                    placeholder="Specify outcome"
                    value={formData.outcomesOther}
                    onChange={(e) =>
                      handleChange("outcomesOther", e.target.value)
                    }
                    className={`${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                    }`}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Section 6 or 7: Referral Details - shown when Non-urgent signposting or Urgent escalation is selected */}
          {(formData.outcomes === "non-urgent-signposting" ||
            formData.outcomes === "urgent-escalation") && (
            <div
              className={`border rounded-2xl p-4 transition-colors duration-300 ${
                isDarkMode
                  ? "border-gray-700 bg-gray-800"
                  : "border-gray-200 bg-white"
              }`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold transition-colors duration-300 ${
                    isDarkMode ? "bg-white text-black" : "bg-black text-white"
                  }`}
                >
                  {!fromKanbanOrList ? "7" : "6"}
                </div>
                <h3
                  className={`text-sm font-semibold transition-colors duration-300 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Referral Details
                </h3>
              </div>

              <div className="space-y-4">
                {/* Referral Destination or Escalation to */}
                <div className="space-y-2">
                  <Label
                    className={`text-sm ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {formData.outcomes === "urgent-escalation"
                      ? "Escalation to"
                      : "Referral Destination"}
                  </Label>

                  {formData.outcomes === "non-urgent-signposting" ? (
                    <>
                      <RadioGroup
                        value={formData.referralDestination}
                        onValueChange={(value) => {
                          handleChange("referralDestination", value);
                          if (value !== "other") {
                            handleChange("referralDestinationOther", "");
                          }
                        }}
                        className="flex flex-col gap-3"
                      >
                        {formData_source.selectOptions.referralDestinations.map(
                          (destination) => (
                            <div
                              key={destination.value}
                              className="flex items-center space-x-2"
                            >
                              <RadioGroupItem
                                value={destination.value}
                                id={`referral-${destination.value}`}
                                className={`${
                                  isDarkMode
                                    ? "border-gray-600 text-white"
                                    : "border-gray-300 text-black"
                                }`}
                              />
                              <label
                                htmlFor={`referral-${destination.value}`}
                                className={`text-sm cursor-pointer ${
                                  isDarkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                {destination.label}
                              </label>
                            </div>
                          ),
                        )}
                      </RadioGroup>

                      {/* Other Referral Destination Text Input - shown when Other is selected */}
                      {formData.referralDestination === "other" && (
                        <div className="mt-3 space-y-2">
                          <Label
                            htmlFor="referral-other-specify"
                            className={`text-sm ${
                              isDarkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Please specify
                          </Label>
                          <Input
                            id="referral-other-specify"
                            type="text"
                            placeholder="Specify referral destination"
                            value={formData.referralDestinationOther}
                            onChange={(e) =>
                              handleChange(
                                "referralDestinationOther",
                                e.target.value,
                              )
                            }
                            className={`${
                              isDarkMode
                                ? "bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                                : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                            }`}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <RadioGroup
                      value={formData.escalationDestination}
                      onValueChange={(value) => {
                        handleChange("escalationDestination", value);
                        if (value !== "other") {
                          handleChange("escalationDestinationOther", "");
                        }
                      }}
                      className="flex flex-col gap-3"
                    >
                      {formData_source.selectOptions.escalationDestinations.map(
                        (destination) => (
                          <div
                            key={destination.value}
                            className="flex items-center space-x-2"
                          >
                            <RadioGroupItem
                              value={destination.value}
                              id={`escalation-${destination.value}`}
                              className={`${
                                isDarkMode
                                  ? "border-gray-600 text-white"
                                  : "border-gray-300 text-black"
                              }`}
                            />
                            <label
                              htmlFor={`escalation-${destination.value}`}
                              className={`text-sm cursor-pointer ${
                                isDarkMode ? "text-gray-300" : "text-gray-700"
                              }`}
                            >
                              {destination.label}
                            </label>
                          </div>
                        ),
                      )}
                    </RadioGroup>
                  )}

                  {/* Other Escalation Text Input - shown when Other is selected for urgent escalation */}
                  {formData.outcomes === "urgent-escalation" &&
                    formData.escalationDestination === "other" && (
                      <div className="mt-3 space-y-2">
                        <Label
                          htmlFor="escalation-other-specify"
                          className={`text-sm ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Please specify
                        </Label>
                        <Input
                          id="escalation-other-specify"
                          type="text"
                          placeholder="Specify escalation destination"
                          value={formData.escalationDestinationOther}
                          onChange={(e) =>
                            handleChange(
                              "escalationDestinationOther",
                              e.target.value,
                            )
                          }
                          className={`${
                            isDarkMode
                              ? "bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                              : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                          }`}
                        />
                      </div>
                    )}
                </div>

                {/* Onward Referral Reason */}
                <div className="space-y-2">
                  <Label
                    htmlFor="referral-reason"
                    className={`text-sm ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Onward Referral Reason
                  </Label>
                  <Input
                    id="referral-reason"
                    type="text"
                    placeholder="Enter referral reason"
                    value={formData.onwardReferralReason}
                    onChange={(e) =>
                      handleChange("onwardReferralReason", e.target.value)
                    }
                    className={`${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                    }`}
                  />
                </div>

                {/* Onward Referral Date and Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label
                      htmlFor="referral-date"
                      className={`text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Onward Referral Date
                    </Label>
                    <div className="relative">
                      <Input
                        id="referral-date"
                        type="date"
                        value={formData.onwardReferralDate}
                        onChange={(e) =>
                          handleChange("onwardReferralDate", e.target.value)
                        }
                        className={`${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      />
                      <Calendar className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 pointer-events-none right-3 top-1/2" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="referral-time"
                      className={`text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Onward Referral Time
                    </Label>
                    <Input
                      id="referral-time"
                      type="time"
                      value={formData.onwardReferralTime}
                      onChange={(e) =>
                        handleChange("onwardReferralTime", e.target.value)
                      }
                      className={`${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    />
                  </div>
                </div>

                {/* Destination ODS Code (Optional) */}
                <div className="space-y-2">
                  <Label
                    htmlFor="ods-code"
                    className={`text-sm ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Destination ODS Code{" "}
                    <span className="text-gray-400">(optional)</span>
                  </Label>
                  <Input
                    id="ods-code"
                    type="text"
                    placeholder="Enter ODS code"
                    value={formData.destinationOdsCode}
                    onChange={(e) =>
                      handleChange("destinationOdsCode", e.target.value)
                    }
                    className={`${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                    }`}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Section 6/7/8: Advice and Support (number depends on whether Referral Details sections are shown) */}
          <div
            className={`border rounded-2xl p-4 transition-colors duration-300 ${
              isDarkMode
                ? "border-gray-700 bg-gray-800"
                : "border-gray-200 bg-white"
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold transition-colors duration-300 ${
                  isDarkMode ? "bg-white text-black" : "bg-black text-white"
                }`}
              >
                {(() => {
                  const hasOutcomeReferral =
                    formData.outcomes === "non-urgent-signposting" ||
                    formData.outcomes === "urgent-escalation";
                  if (!fromKanbanOrList && hasOutcomeReferral) return "8";
                  if (!fromKanbanOrList && !hasOutcomeReferral) return "7";
                  if (fromKanbanOrList && hasOutcomeReferral) return "7";
                  return "6";
                })()}
              </div>
              <h3
                className={`text-sm font-semibold transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Advice and Support
              </h3>
            </div>

            <div className="space-y-4">
              {/* Information Panel */}
              <Alert
                className={`${
                  isDarkMode
                    ? "bg-blue-900/20 border-blue-800 text-blue-200"
                    : "bg-blue-50 border-blue-200 text-blue-900"
                }`}
              >
                <Info className="w-4 h-4" />
                <AlertDescription className="text-sm">
                  {formData_source.messages.adviceReminder}
                </AlertDescription>
              </Alert>

              {/* Advice Given Checkbox */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="advice-given"
                  checked={formData.adviceGiven}
                  onCheckedChange={(checked) =>
                    handleChange("adviceGiven", checked === true)
                  }
                  className={`${
                    isDarkMode
                      ? "border-gray-600 data-[state=checked]:bg-blue-600"
                      : "border-gray-300 data-[state=checked]:bg-blue-600"
                  }`}
                />
                <label
                  htmlFor="advice-given"
                  className={`text-sm cursor-pointer ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Patient has been given this advice
                </label>
              </div>

              {/* Who was the advice provided to */}
              <div className="space-y-2">
                <Label
                  className={`text-sm ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Who was the advice provided to?
                </Label>
                <RadioGroup
                  value={formData.adviceProvidedTo}
                  onValueChange={(value) =>
                    handleChange("adviceProvidedTo", value)
                  }
                  className="flex gap-4"
                >
                  {formData_source.selectOptions.adviceProvidedToOptions.map(
                    (option) => (
                      <div
                        key={option.value}
                        className="flex items-center space-x-2"
                      >
                        <RadioGroupItem
                          value={option.value}
                          id={`advice-to-${option.value}`}
                          className={`${
                            isDarkMode
                              ? "border-gray-600 text-white"
                              : "border-gray-300 text-black"
                          }`}
                        />
                        <label
                          htmlFor={`advice-to-${option.value}`}
                          className={`text-sm cursor-pointer ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          {option.label}
                        </label>
                      </div>
                    ),
                  )}
                </RadioGroup>
              </div>
            </div>
          </div>

          {/* Section 7/8: Notes (number depends on whether Referral Details is shown) */}
          <div
            className={`border rounded-2xl p-4 transition-colors duration-300 ${
              isDarkMode
                ? "border-gray-700 bg-gray-800"
                : "border-gray-200 bg-white"
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold transition-colors duration-300 ${
                  isDarkMode ? "bg-white text-black" : "bg-black text-white"
                }`}
              >
                {formData.outcomes === "non-urgent-signposting" ||
                formData.outcomes === "urgent-escalation"
                  ? "8"
                  : "7"}
              </div>
              <h3
                className={`text-sm font-semibold transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Notes
              </h3>
            </div>

            <div className="space-y-4">
              {/* Plan & requested actions */}
              <div className="space-y-2">
                <Label
                  htmlFor="plan-actions"
                  className={`text-sm font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Plan & requested actions{" "}
                  <span className="text-gray-400">(Optional)</span>
                </Label>
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {formData_source.messages.planAndActionsNote}
                </p>
                <Textarea
                  id="plan-actions"
                  placeholder="Enter plan and requested actions..."
                  value={formData.planAndRequestedActions}
                  onChange={(e) =>
                    handleChange("planAndRequestedActions", e.target.value)
                  }
                  rows={4}
                  className={`${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                  }`}
                />
              </div>

              {/* Internal notes */}
              <div className="space-y-2">
                <Label
                  htmlFor="internal-notes"
                  className={`text-sm font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Internal notes{" "}
                  <span className="text-gray-400">(Optional)</span>
                </Label>
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {formData_source.messages.internalNotesNote}
                </p>
                <Textarea
                  id="internal-notes"
                  placeholder="Enter internal notes..."
                  value={formData.internalNotes}
                  onChange={(e) =>
                    handleChange("internalNotes", e.target.value)
                  }
                  rows={4}
                  className={`${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Edit Prescription Modal */}
          {editingPrescriptionIndex !== null && (
            <PrescriptionModal
              editMode={true}
              isOpen={editingPrescriptionIndex !== null}
              onOpenChange={(open) => {
                if (!open) setEditingPrescriptionIndex(null);
              }}
              existingPrescription={prescriptions[editingPrescriptionIndex]}
              onSave={handlePrescriptionSave}
              isDarkMode={isDarkMode}
            />
          )}

          {/* View Prescription Dialog */}
          <Dialog
            open={viewingPrescriptionIndex !== null}
            onOpenChange={(open) => {
              if (!open) setViewingPrescriptionIndex(null);
            }}
          >
            <DialogContent
              className={`max-w-4xl max-h-[90vh] overflow-auto ${
                isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white"
              }`}
            >
              <DialogHeader>
                <DialogTitle
                  className={isDarkMode ? "text-white" : "text-gray-900"}
                >
                  Prescription Preview
                </DialogTitle>
              </DialogHeader>
              {viewingPrescriptionIndex !== null && (
                <div
                  className="prescription-preview"
                  dangerouslySetInnerHTML={{
                    __html: generatePrescriptionHTML({
                      ...formData_source.prescriptionPreview,
                      items:
                        prescriptions[viewingPrescriptionIndex]
                          .prescriptionItems,
                      timestamp: new Date(),
                    }),
                  }}
                />
              )}
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Data Sharing Details Dialog */}
      <Dialog
        open={isDataSharingPopupOpen}
        onOpenChange={setIsDataSharingPopupOpen}
      >
        <DialogContent
          className={`max-w-2xl ${
            isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white"
          }`}
        >
          <DialogHeader>
            <DialogTitle
              className={`text-lg font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {formData_source.messages.dataSharingAlert.title}
            </DialogTitle>
          </DialogHeader>
          <div
            className={`space-y-4 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
          >
            <ul className="space-y-3 list-none">
              {formData_source.messages.dataSharingAlert.points.map(
                (point, index) => (
                  <li key={index} className="flex gap-3">
                    <span className="flex-shrink-0 font-bold text-blue-600 dark:text-blue-400">
                      •
                    </span>
                    <span className="text-sm">{point}</span>
                  </li>
                ),
              )}
            </ul>
          </div>
        </DialogContent>
      </Dialog>

      {/* Complete Button */}
      <div className="flex justify-end pt-4">
        <Button
          type="submit"
          disabled={addConsultationSummaryLoader || consultationStatus === 1}
          className={`
    ${
      isDarkMode
        ? "bg-white text-black hover:bg-gray-200"
        : "bg-black text-white hover:bg-gray-800"
    }
    ${addConsultationSummaryLoader ? "opacity-60 cursor-not-allowed" : ""}
  `}
        >
          {addConsultationSummaryLoader ? "Please wait..." : "Complete"}
        </Button>
      </div>
    </form>
  );
}
